/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { useCallback, useEffect, useMemo, useState } from 'react'

import { Alert, Autocomplete, IconButton, Snackbar, TextField } from '@mui/material'
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useLocation, useNavigate } from 'react-router-dom'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { ROLE_LEVEL } from 'constants/roles'
import { useAuth } from 'context/auth'
import { UserRole } from 'context/auth/types'
import { ROUTE_PATHS } from 'routes'
import { handleUserSigning } from 'utils/signing'

function AdminUserControl(): JSX.Element {
	const { user, setUser } = useAuth()
	const [notificationVisible, setNotificationVisible] = useState<boolean>(false)
	const [notificationType, setNotificationType] = useState<'success' | 'error'>('success')
	const [notificationMessage, setNotificationMessage] = useState<string>('')
	const location = useLocation()

	const navigate = useNavigate()
	useEffect(() => {
		const state = location.state as {
			notification?: { type: 'success' | 'error'; message: string }
		}
		if (state?.notification) {
			setNotificationType(state.notification.type || 'success')
			setNotificationMessage(state.notification.message || '')
			setNotificationVisible(true)

			// Clear the state to prevent showing the notification again on refresh
			navigate(location.pathname, { replace: true, state: {} })
		}
	}, [location.state, location.pathname, navigate])

	// Define useQuery hook before callbacks that use refetch
	const { refetch } = useQuery({
		queryKey: ['account-users'] as const,
		queryFn: async () => handleUserSigning(user.auth, setUser),
	})

	const handleDeleteUser = useCallback(
		async (uid: string) => {
			try {
				const response = await axios.delete<{ message: string; quantity: number }>(
					`${process.env.REACT_APP_API_URL}/account/user`,
					{
						data: {
							uid: uid,
						},
						headers: {
							Authorization: await user.auth.getIdToken(),
						},
					}
				)
				if (response.status === 204) {
					console.log('✅ User deleted successfully:', uid)
					// refresh the user data or navigate to a different page
					if (user.data.organisation && user.data.organisation.users) {
						user.data.organisation.users = user.data.organisation.users.filter(
							account => account.uid !== uid
						)
					}
				}
				setNotificationType('success')
				setNotificationMessage(`User with UID: ${uid} deleted successfully.`)
				setNotificationVisible(true)
				refetch() // Refetch to update the user list
			} catch (error) {
				console.error('❌ Error deleting user:', error)
				setNotificationType('error')
				setNotificationVisible(true)
				if (axios.isAxiosError(error)) {
					setNotificationMessage(
						error.response?.data?.message || 'An error occurred while deleting the user.'
					)
				} else {
					setNotificationMessage('An unexpected error occurred.')
				}
			}
		},
		[refetch, user.auth, user.data.organisation]
	)

	const handleUpdateUserRole = useCallback(
		async (uid: string, newRole: UserRole) => {
			try {
				const response = await axios.put<{ message: string; quantity: number }>(
					`${process.env.REACT_APP_API_URL}/account/user`,
					{
						uid: uid,
						role: newRole,
					},
					{
						headers: {
							Authorization: await user.auth.getIdToken(),
						},
					}
				)
				if (response.status === 200) {
					console.log('✅ User role updated successfully:', uid, newRole)
					// Update the user data in the local state
					const updatedUser = user.data.organisation?.users.find(account => account.uid === uid)
					if (updatedUser) {
						updatedUser.role = newRole
					}
				}
				setNotificationType('success')
				setNotificationMessage(`User with UID: ${uid} role updated to ${newRole}.`)
				setNotificationVisible(true)
				refetch()
			} catch (error) {
				console.error('❌ Error updating user role:', error)
				setNotificationType('error')
				setNotificationVisible(true)
				if (axios.isAxiosError(error)) {
					setNotificationMessage(
						error.response?.data?.message || 'An error occurred while updating the user role.'
					)
				} else {
					setNotificationMessage('An unexpected error occurred.')
				}
			}
		},
		[refetch, user.auth, user.data.organisation?.users]
	)

	const handleClose = useCallback(() => {
		setNotificationVisible(false)
	}, [])

	const usersList = useMemo(
		() => user.data.organisation?.users || [],
		[user.data.organisation?.users]
	)

	return (
		<Card id="admin-user-control">
			<Snackbar
				open={notificationVisible}
				autoHideDuration={3000}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				sx={{ justifyContent: 'center', alignItems: 'center' }}
			>
				<Alert
					onClose={handleClose}
					severity={notificationType === 'success' ? 'success' : 'error'}
					variant="filled"
					sx={{ width: '100%' }}
				>
					{notificationType === 'success' ? 'Success' : 'Error'}
					{notificationMessage && (
						<MDTypography variant="body2" color="light">
							{notificationMessage}
						</MDTypography>
					)}
				</Alert>
			</Snackbar>
			<MDBox p={3} lineHeight={1}>
				<MDBox
					display={'flex'}
					flexDirection={'row'}
					justifyContent={'space-between'}
					alignItems={'center'}
				>
					<MDBox>
						<MDBox mb={1}>
							<MDTypography variant="h5">Admin User Control</MDTypography>
						</MDBox>
						<MDTypography variant="button" color="text" fontWeight="regular">
							This is the users associated with your organisation.
						</MDTypography>
					</MDBox>
					<MDButton
						color="primary"
						variant="outlined"
						onClick={() => navigate(ROUTE_PATHS.ACCOUNT.NEW_USER)}
					>
						+ add new user
					</MDButton>
				</MDBox>
			</MDBox>
			<MDBox pb={3} px={3} sx={{ overflow: 'auto' }}>
				{usersList.map((account, index) => {
					return (
						<MDBox
							key={index}
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							width={{ xs: 'max-content', sm: '100%' }}
							py={2}
						>
							<MDBox display="flex" alignItems="center" width={'100%'}>
								<MDBox textAlign="center" color="text" px={{ xs: 0, md: 1.5 }} opacity={0.6}>
									<Icon color={ROLE_LEVEL[2].includes(account.role) ? 'primary' : 'secondary'}>
										star
									</Icon>
								</MDBox>
								<MDBox
									height="100%"
									display={'flex'}
									flex={4}
									flexDirection={'column'}
									ml={2}
									lineHeight={1}
									mr={2}
								>
									<MDTypography
										display="block"
										variant="button"
										fontWeight="regular"
										color={ROLE_LEVEL[2].includes(account.role) ? 'primary' : 'secondary'}
									>
										{account.email}
									</MDTypography>
									<MDTypography variant="caption" color="text">
										UID: {account.uid}
									</MDTypography>

									<MDTypography variant="caption" color="text">
										Name: {account.name || 'N/A'}
									</MDTypography>
								</MDBox>
								<MDBox display="flex" alignItems="center" flex={4} pl={10}>
									<MDBox display="flex" alignItems="center" justifyContent="flex-end" mr={2}>
										<Autocomplete
											options={ROLE_LEVEL.noOwner}
											disableClearable
											value={account.role}
											onChange={(_, newValue) => {
												if (newValue && newValue !== account.role) {
													handleUpdateUserRole(account.uid, newValue as UserRole)
												}
											}}
											getOptionLabel={option => option.charAt(0) + option.slice(1).toLowerCase()}
											renderInput={params => (
												<TextField
													{...params}
													variant="outlined"
													size="small"
													sx={{ minWidth: 120 }}
													label="Role"
												/>
											)}
											size="small"
											disabled={account.role === 'OWNER'}
											sx={{ minWidth: 200 }}
										/>
									</MDBox>
								</MDBox>
								<MDBox display="flex" flex={1} justifyContent="flex-end" alignItems={'center'}>
									{account.role !== 'OWNER' && (
										<IconButton
											size="medium"
											aria-label="delete"
											color="error"
											onClick={() => {
												handleDeleteUser(account.uid)
											}}
										>
											<Icon fontSize="medium">delete_outline</Icon>
										</IconButton>
									)}
								</MDBox>
							</MDBox>
							<Divider />
						</MDBox>
					)
				})}
			</MDBox>
		</Card>
	)
}

export default AdminUserControl
